import { PermissionResolvable, SlashCommandBuilder, SlashCommandOptionsOnlyBuilder, SlashCommandSubcommandsOnlyBuilder, AutocompleteInteraction, StringSelectMenuInteraction, UserSelectMenuInteraction, ChannelSelectMenuInteraction, RoleSelectMenuInteraction, MentionableSelectMenuInteraction, ButtonInteraction, ContextMenuCommandBuilder, UserContextMenuCommandInteraction, MessageContextMenuCommandInteraction, ModalSubmitInteraction, Message, Collection, Interaction, ChatInputCommandInteraction, LocaleString, GuildMember, APIInteractionGuildMember } from "discord.js"
import { ButtonCommand, ContextMenuCommand, MessageCommand, ModalCommand, SelectMenuCommand, SlashCommand } from "../structures";

export interface AdvancedCommandOptions {
    runIn?: RunIn;
    ratelimit?: Ratelimit;
    restricted?: boolean;
    users?: {
        deny?: string[];
        allow?: string[];
    };
    roles?: {
        deny?: string[];
        allow?: string[];
    };
    permissions?: PermissionsList;
    help?: Help;
}

export enum RunIn {
    DM,
    Guild,
    Any
}

interface PermissionsList {
    allow?: PermissionResolvable[];
    deny?: PermissionResolvable[];
    allowMode?: PermissionsMode;
    denyMode?: PermissionsMode;
}

export enum PermissionsMode {
    MatchNone,
    MatchOne,
    MatchAll
}

interface Ratelimit {
    user?: number;
    guild?: number;
    channel?: number;
    global?: number;
}

export interface Help {
    name?: string;
    description?: string;
    usage: string;
    category: string;
    examples: string[];
}

export interface BaseCommandOptions {
    enabled?: boolean;
    options?: AdvancedCommandOptions;
}

export interface BaseCommand extends BaseCommandOptions {
    commandType: "slash" | "contextMenuUser" | "contextMenuMessage" | "message" | "selectMenu" | "button" | "modal";
    isIDBased: () => this is IDBasedCommand<any>
    isSlashCommand: () => this is ISlashCommand 
    isContextMenuCommand: () => this is IContextMenuCommand<"user" | "message"> 
    isMessageCommand: () => this is IMessageCommand<any>
}

export type MessageArguments<S extends string> = S extends `<${infer Start}> ${infer Rest}` ? 
    process<Start, true> & MessageArguments<Rest> :
    S extends `[${infer Start}] ${infer Rest}` ?
    process<Start, false> & MessageArguments<Rest> :
    S extends `<${infer Start}>` ?
    process<Start, true> :
    S extends `[${infer Start}]` ?
    process<Start, false> :
    never

type process<arg extends string, required extends boolean> = arg extends `${infer name}:${infer options}` ? 
options extends `${infer _},${infer __}` ? 
    required extends true ? { [K in name]: CreateUnionFromCommaSeparated<options> } : { [K in name]?: CreateUnionFromCommaSeparated<options> }
        : required extends true ? { [K in name]: boolean } : { [K in name]?: boolean }
: required extends true ? { [K in Remove<arg>]: string } : { [K in Remove<arg>]?: string }

// Message command
type Remove<S extends string> = RemoveRegex<RemoveEllipsis<S>>;
type RemoveRegex<T extends string> = T extends `${infer WithoutRegex}(${infer _})` ? WithoutRegex : T;
type RemoveEllipsis<T extends string> = T extends `${infer WithoutEllipsis}...` ? WithoutEllipsis : T;

type CreateUnionFromCommaSeparated<T extends string> = T extends `${infer First},${infer Rest}`? First | CreateUnionFromCommaSeparated<Rest>: T;

export interface MessageCommandOptions<T extends string> {
    name: string;
    readableName?: string;
    description: string;
    aliases?: string[];
    arguments?: T;
    nameLocalizations?: Partial<Record<LocaleString, string>>;
}

export interface IMessageCommandOptions<T extends string> extends BaseCommandOptions {
    data: MessageCommandOptions<T>;
    run: (message: Message, args: MessageArguments<T>) => any | Promise<any>;
}

export interface IMessageCommand<T extends string> extends BaseCommand {
    commandType: "message";
    data: MessageCommandOptions<T>;
    run: (message: Message, args: MessageArguments<T>) => any | Promise<any>;
}

// Command Interactions

export interface ISlashCommandOptions extends BaseCommandOptions {
    data: SlashCommandBuilder | SlashCommandOptionsOnlyBuilder | SlashCommandSubcommandsOnlyBuilder;
    run: (interaction: ChatInputCommandInteraction<"cached">) => any | Promise<any>;
    autocomplete?: Record<string, (interaction: AutocompleteInteraction<"cached">, value: string) => any | Promise<any>>;
}

export interface ISlashCommand extends BaseCommand {
    commandType: "slash";
    data: SlashCommandBuilder | SlashCommandOptionsOnlyBuilder | SlashCommandSubcommandsOnlyBuilder;
    run: (interaction: ChatInputCommandInteraction<"cached">) => any | Promise<any>;
    autocomplete?: Record<string, (interaction: AutocompleteInteraction<"cached">, value: string) => any | Promise<any>>;
}

export interface IContextMenuCommandOptions<T extends "user" | "message"> extends BaseCommandOptions {
    data: ContextMenuCommandBuilder;
    type: T;
    run: (interaction: T extends "user" ? UserContextMenuCommandInteraction<"cached"> : MessageContextMenuCommandInteraction<"cached">) => any | Promise<any>;
}

export interface IContextMenuCommand<T extends "user" | "message"> extends BaseCommand {
    commandType: T extends "user" ? "contextMenuUser" : "contextMenuMessage";
    data: ContextMenuCommandBuilder;
    type: T;
    run: (interaction: T extends "user" ? UserContextMenuCommandInteraction<"cached"> : MessageContextMenuCommandInteraction<"cached">) => any | Promise<any>;
    isUserContextMenuCommand: () => this is IContextMenuCommand<"user">;
    isMessageContextMenuCommand: () => this is IContextMenuCommand<"message">;
}

// ID based interactions

export interface SelectMenus {
    string: StringSelectMenuInteraction<"cached">;
    user: UserSelectMenuInteraction<"cached">;
    channel: ChannelSelectMenuInteraction<"cached">;
    role: RoleSelectMenuInteraction<"cached">;
    mentionable: MentionableSelectMenuInteraction<"cached">;
}

export interface IDBasedCommandOptions<T extends string[]> extends Omit<BaseCommandOptions, "options"> {
    id: string;
    /**
     * If set to true, checks if the user is the same one in the user argument.
     */
    user?: boolean;
    arguments?: T;
}

export interface IDBasedCommand<T extends string[]> extends Omit<BaseCommand, "options">, IDBasedCommandOptions<T> {
    id: string;
    /**
     * If set to true, checks if the user is the same one in the user argument.
     */
    user?: boolean;
    arguments?: T;
    isSelectMenuCommand: () => this is ISelectMenuCommand<any, T>
    isButtonCommand: () => this is IButtonCommand<T> 
    isModalCommand: () => this is IModalCommand<T>
}

export interface ISelectMenuCommandOptions<X extends keyof SelectMenus, T extends string[]> extends IDBasedCommandOptions<T> {
    type: X;
    run: (interaction: SelectMenus[X], args: Record<T[number], string | undefined>) => any | Promise<any>;
}

export interface ISelectMenuCommand<X extends keyof SelectMenus, T extends string[]> extends IDBasedCommand<T> {
    commandType: "selectMenu";
    type: X;
    run: (interaction: SelectMenus[X], args: Record<T[number], string | undefined>) => any | Promise<any>;
    isStringSelectMenuCommand: () => this is ISelectMenuCommand<"string", T>;
    isUserSelectMenuCommand: () => this is ISelectMenuCommand<"user", T>;
    isChannelSelectMenuCommand: () => this is ISelectMenuCommand<"channel", T>;
    isRoleSelectMenuCommand: () => this is ISelectMenuCommand<"role", T>;
    isMentionableSelectMenuCommand: () => this is ISelectMenuCommand<"mentionable", T>;
}

export interface IButtonCommandOptions<T extends string[]> extends IDBasedCommandOptions<T> {
    run: (interaction: ButtonInteraction<"cached">, args: Record<T[number], string | undefined>) => any | Promise<any>;
}

export interface IButtonCommand<T extends string[]> extends IDBasedCommand<T> {
    commandType: "button";
    run: (interaction: ButtonInteraction<"cached">, args: Record<T[number], string | undefined>) => any | Promise<any>;
}

export interface IModalCommandOptions<T extends string[]> extends IDBasedCommandOptions<T> {
    run: (interaction: ModalSubmitInteraction<"cached">, args: Record<T[number], string | undefined>) => any | Promise<any>;
}

export interface IModalCommand<T extends string[]> extends IDBasedCommand<T> {
    commandType: "modal";
    run: (interaction: ModalSubmitInteraction<"cached">, args: Record<T[number], string | undefined>) => any | Promise<any>;
}

export type AnyCommand = MessageCommand<any> | SlashCommand | ContextMenuCommand<"user"> | ContextMenuCommand<"message"> | SelectMenuCommand<keyof SelectMenus, any> | ButtonCommand<any> | ModalCommand<any>;

export interface ICommandManager {
    stores: {
        slash: Collection<string, ISlashCommand>;
        contextMenuUser: Collection<string, IContextMenuCommand<"user">>;
        contextMenuMessage: Collection<string, IContextMenuCommand<"message">>;
        message: Collection<string, IMessageCommand<any>>;
        selectMenu: Collection<string, ISelectMenuCommand<keyof SelectMenus, any>>;
        button: Collection<string, IButtonCommand<any>>;
        modal: Collection<string, IModalCommand<any>>;
    }

    ratelimits: {
        user: Collection<string, {
            command: string;
            type: "slash" | "contextMenuUser" | "contextMenuMessage" | "message";
            expires: number;
        }[]>;
        guild: Collection<string, {
            command: string;
            type: "slash" | "contextMenuUser" | "contextMenuMessage" | "message";
            expires: number;
        }[]>;
        channel: Collection<string, {
            command: string;
            type: "slash" | "contextMenuUser" | "contextMenuMessage" | "message";
            expires: number;
        }[]>;
        global: Collection<string, {
            command: string;
            type: "slash" | "contextMenuUser" | "contextMenuMessage" | "message";
            expires: number;
        }[]>;
    }

    run(intOrMsg: Interaction<"cached"> | Message, { commandName, precommand, postcommand, postrun, behavior, translations }: {
        commandName?: string,
        precommand?: (intOrMsg: Interaction<"cached"> | Message) => boolean,
        postcommand?: (intOrMsg: Interaction<"cached"> | Message) => boolean,
        postprocess?: (intOrMsg: Interaction<"cached"> | Message) => boolean,
        postrun?: (intOrMsg: Interaction<"cached"> | Message) => boolean,
        behavior?: "preferIgnore" | "preferReply" | "auto",
        translations?: Partial<Record<"notallowed" | "ratelimit" | "unavailable", Partial<Record<LocaleString, string>>>>;
    }): any | Promise<any>;

    checkPermissions(command: MessageCommand<any> | SlashCommand | ContextMenuCommand<"user" | "message">, user: GuildMember | APIInteractionGuildMember): boolean;
    
    ratelimit(command: MessageCommand<any> | SlashCommand | ContextMenuCommand<any>, userId: string): boolean;

    parseArgs<T extends string>(args: string, format: T): MessageArguments<T>;
    parseArgs<T extends string[]>(args: string, format: T): { [K in T[number]]?: string } & { id: string; userId?: string };
    parseArgs<T extends string | string[]>(args: string, format: T): Record<string, string | boolean | undefined> ;
}